/**
 * Geração e validação automática de JSON-LD (FAQPage) para /perguntas-frequentes.
 *
 * Objetivos:
 * - Emitir um único FAQPage canônico com TODAS as perguntas (recomendação do Google).
 * - Expor por grupo via `hasPart: ItemList` com âncoras estáveis (#groupId, #faqId),
 *   permitindo que cada grupo novo seja indexado sem duplicar FAQPage na mesma URL.
 * - Rodar validação em runtime (dev) e via script build-time para pegar regressões
 *   antes do deploy.
 */
import type { FaqGroup, FaqItem } from "./faq-data";

export type FaqJsonLdOptions = {
  pageUrl: string;
  siteUrl: string;
  pageTitle: string;
  pageDescription: string;
  inLanguage?: string;
};

export type FaqValidationIssue = {
  level: "error" | "warning";
  message: string;
};

/**
 * Regras estruturais que refletem o que o Rich Results Test exige em FAQPage.
 * Retorna lista vazia quando o dataset está válido.
 */
export function validateFaqGroups(groups: FaqGroup[]): FaqValidationIssue[] {
  const issues: FaqValidationIssue[] = [];
  const seenIds = new Set<string>();
  const seenQuestions = new Set<string>();

  if (groups.length === 0) {
    issues.push({ level: "error", message: "FAQ_GROUPS está vazio." });
    return issues;
  }

  for (const g of groups) {
    if (!g.id) issues.push({ level: "error", message: `Grupo sem id: "${g.title}"` });
    if (!g.title) issues.push({ level: "error", message: `Grupo "${g.id}" sem title.` });
    if (!Array.isArray(g.items) || g.items.length === 0) {
      issues.push({ level: "error", message: `Grupo "${g.id}" sem itens.` });
      continue;
    }
    for (const it of g.items) {
      if (!it.id) {
        issues.push({ level: "error", message: `Item sem id em "${g.id}" (pergunta: "${it.q?.slice(0, 60)}").` });
      } else if (seenIds.has(it.id)) {
        issues.push({ level: "error", message: `ID duplicado: "${it.id}".` });
      } else {
        seenIds.add(it.id);
      }
      if (!it.q?.trim()) {
        issues.push({ level: "error", message: `Item "${it.id}" sem pergunta.` });
      } else {
        const norm = it.q.trim().toLowerCase();
        if (seenQuestions.has(norm)) {
          issues.push({ level: "warning", message: `Pergunta duplicada: "${it.q}".` });
        } else {
          seenQuestions.add(norm);
        }
      }
      if (!it.a?.trim()) {
        issues.push({ level: "error", message: `Item "${it.id}" sem resposta.` });
      } else if (it.a.trim().length < 20) {
        issues.push({ level: "warning", message: `Resposta muito curta em "${it.id}" (<20 chars).` });
      }
    }
  }

  return issues;
}

function questionNode(url: string, item: FaqItem) {
  return {
    "@type": "Question",
    "@id": `${url}#${item.id}`,
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.a,
    },
  };
}

/**
 * Constrói o JSON-LD completo da página: FAQPage canônico + ItemList por grupo.
 * Cada grupo novo é incluído automaticamente sem necessidade de editar o schema.
 */
export function buildFaqPageJsonLd(groups: FaqGroup[], opts: FaqJsonLdOptions) {
  const {
    pageUrl,
    siteUrl,
    pageTitle,
    pageDescription,
    inLanguage = "pt-BR",
  } = opts;

  const allItems = groups.flatMap((g) => g.items);

  const faqPage = {
    "@type": "FAQPage",
    "@id": `${pageUrl}#faqpage`,
    url: pageUrl,
    name: pageTitle,
    description: pageDescription,
    inLanguage,
    isPartOf: { "@type": "WebSite", "@id": `${siteUrl}/#website` },
    mainEntity: allItems.map((it) => questionNode(pageUrl, it)),
  };

  const groupLists = groups.map((g) => ({
    "@type": "ItemList",
    "@id": `${pageUrl}#list-${g.id}`,
    name: g.title,
    description: g.description,
    url: `${pageUrl}#${g.id}`,
    numberOfItems: g.items.length,
    itemListElement: g.items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.q,
      url: `${pageUrl}#${it.id}`,
      item: { "@id": `${pageUrl}#${it.id}` },
    })),
  }));

  return {
    "@context": "https://schema.org",
    "@graph": [faqPage, ...groupLists],
  };
}
